import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { createElement } from "react";
import { ru } from "./ru";
import { kk } from "./kk";
import { en } from "./en";

export type Lang = "ru" | "kk" | "en";

export type Dict = typeof ru;

const DICTS: Record<Lang, Dict> = { ru, kk, en };

const STORAGE_KEY = "landing-lang";

function detectInitialLang(): Lang {
  if (typeof window === "undefined") return "ru";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "ru" || saved === "kk" || saved === "en") return saved;
  } catch {
    /* ignore */
  }
  const nav = (navigator.language || "ru").toLowerCase();
  if (nav.startsWith("kk") || nav.startsWith("kz")) return "kk";
  if (nav.startsWith("ru") || nav.startsWith("uk") || nav.startsWith("be")) return "ru";
  return "en";
}

interface LangContextValue {
  lang: Lang;
  setLang: (next: Lang) => void;
  t: Dict;
}

const LangContext = createContext<LangContextValue | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang());

  const setLang = (next: Lang) => {
    setLangState(next);
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
  }, [lang]);

  return createElement(
    LangContext.Provider,
    { value: { lang, setLang, t: DICTS[lang] } },
    children,
  );
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be inside <LangProvider>");
  return ctx;
}

export const LANG_LABELS: Record<Lang, string> = {
  ru: "Русский",
  kk: "Қазақша",
  en: "English",
};

export const LANG_SHORT: Record<Lang, string> = {
  ru: "RU",
  kk: "KZ",
  en: "EN",
};
